// Which language the interface speaks. English is the source (`en/` defines the shape,
// `it/` is typed against it — a missing translation does not compile); Italian is the
// default, because the product is used in Italian. Resolved ONCE at module evaluation:
// a language switcher writes localStorage and reloads.

export type Locale = 'en' | 'it'

export const LOCALES: readonly Locale[] = ['en', 'it']

/** Italian, because that is the language the product is used in. English is the source. */
export const DEFAULT_LOCALE: Locale = 'it'

/** Where an explicit choice is remembered, when a language switcher exists. */
export const LOCALE_STORAGE_KEY = 'volee-locale'

function isLocale(value: string | null): value is Locale {
  return value !== null && (LOCALES as readonly string[]).includes(value)
}

/**
 * The locale to use: an explicit choice, otherwise the browser's, otherwise the default.
 *
 * Every step is guarded because this runs at module evaluation: in a test environment
 * `localStorage` can throw (Safari in private mode does), and there is no interface language
 * worth crashing the boot over.
 */
export function resolveLocale(): Locale {
  try {
    const stored = globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY) ?? null
    if (isLocale(stored)) return stored
  } catch {
    // A storage that refuses to be read is not a reason to fail: fall through to the browser.
  }

  const preferred = globalThis.navigator?.languages ?? []
  for (const tag of preferred) {
    // `it-IT` and `it` both mean Italian: the region is not a language.
    const language = tag.split('-')[0]?.toLowerCase() ?? ''
    if (isLocale(language)) return language
  }

  return DEFAULT_LOCALE
}

/** The active locale for this session. */
export const locale: Locale = resolveLocale()
